import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { injectIntl } from "react-intl";
import Drawer from "@material-ui/core/Drawer";
import Fab from "@material-ui/core/Fab";
import Typography from "@material-ui/core/Typography";
import Button from "@material-ui/core/Button";
import Box from "@material-ui/core/Box";
import IconButton from "components/icon-button";
import { makeStyles } from "@material-ui/core/styles";
import CodeIcon from "@material-ui/icons/Code";
import CloseIcon from "@material-ui/icons/Close";
import SaveIcon from "@material-ui/icons/Save";
import ReactJson from "react-json-view";
import { buildFilePayload } from "../utils/buildFilePayload";

const useStyles = makeStyles(theme => ({
  fab: {
    position: "fixed",
    bottom: theme.spacing(10),
    right: theme.spacing(3),
    zIndex: 1000,
  },
  drawer: {
    width: "60vw",
    maxWidth: "800px",
  },
  drawerPaper: {
    width: "60vw",
    maxWidth: "800px",
  },
  drawerHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: theme.spacing(2),
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  drawerContent: {
    padding: theme.spacing(2),
    height: "calc(100vh - 140px)",
    overflow: "auto",
  },
  drawerFooter: {
    display: "flex",
    justifyContent: "flex-end",
    gap: theme.spacing(2),
    padding: theme.spacing(2),
    borderTop: `1px solid ${theme.palette.divider}`,
  },
  jsonEditor: {
    backgroundColor: "#f5f5f5",
    padding: theme.spacing(2),
    borderRadius: theme.spacing(1),
    "& .react-json-view": {
      fontSize: "14px !important",
    },
  },
}));

const JsonEditorDrawer = ({ intl, values, onSave }) => {
  const classes = useStyles();
  const [isOpen, setIsOpen] = useState(false);
  const [editedJson, setEditedJson] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(
    () => {
      if (isOpen) {
        try {
          setEditedJson(buildFilePayload(values));
        } catch {
          setEditedJson(values.file || {});
        }
        setHasChanges(false);
      }
    },
    [isOpen, values.file],
  );

  const handleOpen = () => {
    setIsOpen(true);
  };

  const handleClose = () => {
    setIsOpen(false);
    setEditedJson(null);
    setHasChanges(false);
  };

  const handleEdit = edit => {
    setEditedJson(edit.updated_src);
    setHasChanges(true);
  };

  const handleAdd = add => {
    setEditedJson(add.updated_src);
    setHasChanges(true);
  };

  const handleDelete = del => {
    setEditedJson(del.updated_src);
    setHasChanges(true);
  };

  const handleSave = () => {
    onSave(editedJson);
    setHasChanges(false);
    handleClose();
  };

  return (
    <>
      <Fab
        color="primary"
        aria-label="json-editor"
        className={classes.fab}
        onClick={handleOpen}
        title={intl.formatMessage({ id: "roomTemplate.jsonEditor.openButton" }, { defaultMessage: "Open JSON Editor" })}
      >
        <CodeIcon />
      </Fab>
      <Drawer
        anchor="right"
        open={isOpen}
        onClose={handleClose}
        classes={{
          paper: classes.drawerPaper,
        }}
      >
        <div className={classes.drawer}>
          <div className={classes.drawerHeader}>
            <Typography variant="h6">
              {intl.formatMessage(
                { id: "roomTemplate.jsonEditor.title" },
                { defaultMessage: "JSON Editor - File Configuration" },
              )}
            </Typography>
            <IconButton onClick={handleClose} size="small">
              <CloseIcon />
            </IconButton>
          </div>
          <div className={classes.drawerContent}>
            <Box mb={2}>
              <Typography variant="body2" color="textSecondary">
                {intl.formatMessage(
                  { id: "roomTemplate.jsonEditor.description" },
                  {
                    defaultMessage:
                      "Edit the file configuration directly in JSON format. Changes will be applied when you click Save.",
                  },
                )}
              </Typography>
            </Box>
            <div className={classes.jsonEditor}>
              {editedJson && (
                <ReactJson
                  src={editedJson}
                  theme="rjv-default"
                  iconStyle="triangle"
                  displayDataTypes={false}
                  displayObjectSize={false}
                  enableClipboard={true}
                  collapsed={1}
                  onEdit={handleEdit}
                  onAdd={handleAdd}
                  onDelete={handleDelete}
                  name="file"
                  style={{
                    fontSize: "14px",
                    fontFamily: "'Monaco', 'Menlo', 'Ubuntu Mono', monospace",
                  }}
                />
              )}
            </div>
          </div>
          <div className={classes.drawerFooter}>
            <Button onClick={handleClose} color="default">
              {intl.formatMessage({ id: "common.cancel" }, { defaultMessage: "Cancel" })}
            </Button>
            <Button
              onClick={handleSave}
              color="primary"
              variant="contained"
              startIcon={<SaveIcon />}
              disabled={!hasChanges}
            >
              {intl.formatMessage({ id: "common.save" }, { defaultMessage: "Save" })}
            </Button>
          </div>
        </div>
      </Drawer>
    </>
  );
};

JsonEditorDrawer.propTypes = {
  intl: PropTypes.object.isRequired,
  values: PropTypes.object.isRequired,
  onSave: PropTypes.func.isRequired,
};

export default injectIntl(JsonEditorDrawer);
